import { useState } from "react";
import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { useSuperAdminOrg, type PendingOrgInvite } from "@/shared/contexts/SuperAdminOrgContext";
import { useToast } from "@/shared/hooks/use-toast";

/**
 * Sino de notificações — versão MÍNIMA, deliberadamente (AB#84509).
 *
 * O ícone existia sem função nenhuma. Aqui ele passa a mostrar o único aviso que
 * hoje tem para onde ir: convite para uma organização adicional. Antes disso o
 * convite só aparecia dentro do seletor de organizações, que é um controle de
 * navegação — quem não tinha motivo para trocar de organização nunca via.
 *
 * O que este componente NÃO faz, e não deve fazer: estado de lido/não lido,
 * persistência, histórico, outros tipos de evento, tempo real. Tudo isso é o épico
 * de notificações (#84880), que substitui esta implementação por uma caixa de
 * verdade. Manter o escopo aqui é o que impede a versão provisória de virar uma
 * meia-implementação difícil de trocar.
 */
export const NotificationsBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingInvites, acceptInvite } = useSuperAdminOrg();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);

  const count = pendingInvites.length;

  const handleAccept = async (invite: PendingOrgInvite) => {
    // Um aceite por vez. Com dois em voo, o `finally` do primeiro a terminar
    // reabilitava os botões e o segundo aceitava de novo o que já estava em curso.
    if (acceptingId) return;
    setAcceptingId(invite.id);
    try {
      await acceptInvite(invite);
      setOpen(false);
      if (!location.pathname.startsWith("/admin/")) navigate("/");
    } catch {
      // Sem isto a falha é muda: o botão volta ao normal e o convite continua na
      // lista, o que se lê como "não fez nada" em vez de "não deu certo".
      toast({
        title: t("notifications.invite.acceptFailed", "Não foi possível aceitar o convite"),
        description: t("common.tryAgain", "Tente novamente em instantes."),
        variant: "destructive",
      });
    } finally {
      setAcceptingId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" title={t("notifications.title", "Notificações")}>
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0" sideOffset={8}>
        <div className="border-b px-3 py-2.5">
          <p className="text-sm font-medium">{t("notifications.title", "Notificações")}</p>
        </div>

        {count === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            {t("notifications.empty", "Nenhuma notificação por enquanto")}
          </p>
        ) : (
          <div className="max-h-[320px] overflow-y-auto p-1">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="rounded-md px-2 py-2 hover:bg-accent/50">
                <p className="text-sm">
                  {t("notifications.invite.title", "Convite para {{organization}}", {
                    organization: invite.organization_name,
                  })}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t(
                    "notifications.invite.description",
                    "Ao aceitar, esta organização fica disponível para você — seus acessos atuais continuam como estão."
                  )}
                </p>
                <button
                  type="button"
                  disabled={!!acceptingId}
                  onClick={() => handleAccept(invite)}
                  className="mt-2 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  {acceptingId === invite.id
                    ? t("common.loading", "Aguarde...")
                    : t("notifications.invite.accept", "Aceitar convite")}
                </button>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
